import { createSelector } from '@ngrx/store';
import { ViewModelService } from 'src/app/shared/view-model.service';
import { CardStatus, NotificationStatus } from 'src/graphql/generated/graphql.generated';
import { NotificationViewModel } from 'src/models/notification-view-model';
import { selectCardSummariesByRegionId } from 'src/store/entityStores/card-selectors';
import { selectAllNotifications } from 'src/store/entityStores/entity-store-selectors';

export function selectNotificationById(notificationId: string) {
  return createSelector(selectAllNotifications, (notifications) =>
    notifications.get(notificationId),
  );
}

export function selectNotificationsByCardId(cardId: string) {
  return createSelector(selectAllNotifications, (notifications) => {
    return notifications
      .filter((n) => n.entityId === cardId || n.cardId === cardId)
      .toIndexedSeq()
      .sortBy((n) => n.dateCreated);
  });
}

export function selectUnsentNotificationsByCardId(cardId: string) {
  return createSelector(selectAllNotifications, (notifications) => {
    return notifications
      .filter((n) => n.cardId === cardId && n.status === NotificationStatus.AwaitingApproval)
      .toIndexedSeq()
      .sortBy((n) => n.dateCreated);
  });
}

export function selectNotificationsByRegionId(regionId: string, ...status: NotificationStatus[]) {
  return createSelector(
    selectAllNotifications,
    selectCardSummariesByRegionId(regionId),
    (notifications, cards) => {
      return (
        notifications
          .filter((n) => status.length === 0 || status.includes(n.status))
          .filter((n) => n.cardId != null && cards.find((c) => c.id === n.cardId) != null)
          .map((n) => {
            return {
              notification: n,
              card: cards.find((c) => c.id === n.cardId),
            };
          })
          .filter((n) => n.card != null && n.card.status !== CardStatus.Closed)
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          .sortBy((n) => n.card!.cardNumber)
          .map((n) => n.notification)
      );
    },
  );
}

export function selectNotificationViewModelsByEntityId(entityId: string, model: ViewModelService) {
  return createSelector(selectAllNotifications, (notifications) => {
    return notifications
      .filter((n) => n.entityId === entityId)
      .map((notification) => new NotificationViewModel(notification, null, model))
      .toIndexedSeq()
      .sortBy((n) => n.dateCreated);
  });
}
